import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TextInput, 
  Group, 
  Button, 
  Stack, 
  Text, 
  ActionIcon, 
  Menu, 
  Drawer,
  Checkbox,
  Select,
  Pagination,
  Badge,
  Tooltip,
  Modal,
  Box,
  ScrollArea,
  LoadingOverlay,
  Title
} from '@mantine/core';
import { 
  IconSearch, 
  IconPlus, 
  IconEye,
  IconEdit, 
  IconFilter,
  IconChevronDown,
  IconChevronUp,
  IconRefresh,
  IconTrash
} from '@tabler/icons-react';
import { useClient } from '../../context/ClientContext';
import { useSalon } from '../../context/SalonContext';
import ClientForm from './ClientForm';
import ClientDetails from './ClientDetails';
import MergeClients from './MergeClients';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import { formatDateTime } from '../../utils/timeUtils';
import styles from './ClientsList.module.css';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';

function ClientsList() {
  const {
    clients,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    sorting,
    setSorting,
    refreshClients,
    deleteClient
  } = useClient();
  const { businessInfo } = useSalon();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);

  useEffect(() => {
    if (selectedClient) {
      // Find the updated version of the selected client in the new clients list
      const updatedClient = clients.find(client => client.id === selectedClient.id);
      if (updatedClient) {
        setSelectedClient(updatedClient);
      }
    }
  }, [clients]);

  const handleSort = (field) => {
    setSorting(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (field) => {
    if (sorting.field !== field) return null;
    return sorting.direction === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
  };

  const columns = [
    { id: 'first_name', label: 'First Name', sortable: true },
    { id: 'last_name', label: 'Last Name', sortable: true },
    { id: 'phone', label: 'Phone', sortable: true },
    { id: 'email', label: 'Email', sortable: true },
    { id: 'last_appointment', label: 'Last Appointment', sortable: true },
    { id: 'is_opted_in_reminder', label: 'Reminder', sortable: true },
    { id: 'is_opted_in_marketing', label: 'Marketing', sortable: true }
  ];

  const handleDeleteConfirm = async () => {
    try {
      await deleteClient(clientToDelete.id);
      setClientToDelete(null);
      // If we're deleting the currently selected client, close any open drawers/modals
      if (selectedClient?.id === clientToDelete.id) {
        setSelectedClient(null);
        setIsCreateModalOpen(false);
      }
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  return (
    <div className={styles.container}>
      <LoadingOverlay visible={loading} overlayBlur={2} />
      <Box p={{ base: 'sm', xs: 'md', sm: 'lg' }}>
        <Stack gap="md">
          <Group className={styles.header} justify="space-between">
            <Title order={2}>Clients</Title>
            <Group gap="sm">
              <TextInput
                placeholder="Search clients..."
                icon={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <Button 
                    leftIcon={<IconFilter size={16} />} 
                    variant="default" 
                    className={styles.button}
                  >
                    Filters
                  </Button>
                </Menu.Target>
                <Menu.Dropdown className={styles.filters}>
                  <Menu.Label>Opt-in Status</Menu.Label>
                  <Menu.Item>
                    <Checkbox
                      label="Reminder"
                      checked={filters.isOptedInReminder}
                      onChange={(e) => setFilters(prev => ({ 
                        ...prev, 
                        isOptedInReminder: e.target.checked 
                      }))}
                    />
                  </Menu.Item>
                  <Menu.Item>
                    <Checkbox
                      label="Marketing"
                      checked={filters.isOptedInMarketing}
                      onChange={(e) => setFilters(prev => ({ 
                        ...prev, 
                        isOptedInMarketing: e.target.checked 
                      }))}
                    />
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              <Button 
                leftIcon={<IconRefresh size={16} />} 
                onClick={refreshClients}
                className={styles.button}
              >
                Refresh
              </Button>

              <Button 
                leftIcon={<IconPlus size={16} />} 
                onClick={() => setIsCreateModalOpen(true)}
                className={styles.button}
              >
                New Client
              </Button>
            </Group>
          </Group>

          {error && (
            <Text color="red" size="sm" className={styles.error}>
              {error}
            </Text>
          )}

          <ScrollArea>
            <Table className={styles.table} striped highlightOnHover>
              <Table.Thead>
                <Table.Tr className={styles.tableHeader}>
                  {columns.map(column => (
                    <Table.Th 
                      key={column.id}
                      className={styles.tableCell}
                      onClick={column.sortable ? () => handleSort(column.id) : undefined}
                      style={{ cursor: column.sortable ? 'pointer' : 'default' }}
                    >
                      <Group gap="xs" noWrap>
                        {column.label}
                        {column.sortable && getSortIcon(column.id)}
                      </Group>
                    </Table.Th>
                  ))}
                  <Table.Th className={styles.tableCell}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {clients.map((client) => (
                  <Table.Tr key={client.id} className={styles.tableRow}>
                    <Table.Td className={styles.tableCell}>{client.first_name}</Table.Td>
                    <Table.Td className={styles.tableCell}>{client.last_name}</Table.Td>
                    <Table.Td className={styles.tableCell}>{formatPhoneNumber(`${client.area_code}${client.phone_number}`)}</Table.Td>
                    <Table.Td className={styles.tableCell}>{client.email || '-'}</Table.Td>
                    <Table.Td className={styles.tableCell}>
                      {client.last_appointment ? formatDateTime(client.last_appointment, businessInfo?.timezone, 'MMM D, YYYY h:mm A') : '-'}
                    </Table.Td>
                    <Table.Td className={styles.tableCell}>
                      <Badge 
                        color={client.is_opted_in_reminder ? 'green' : 'red'}
                        className={styles.badge}
                      >
                        {client.is_opted_in_reminder ? 'Yes' : 'No'}
                      </Badge>
                    </Table.Td>
                    <Table.Td className={styles.tableCell}>
                      <Badge 
                        color={client.is_opted_in_marketing ? 'green' : 'red'}
                        className={styles.badge}
                      >
                        {client.is_opted_in_marketing ? 'Yes' : 'No'}
                      </Badge>
                    </Table.Td>
                    <Table.Td className={styles.tableCell}>
                      <Group gap="xs" className={styles.actionButtons}>
                        <Tooltip label="View Details">
                          <ActionIcon
                            onClick={() => {
                              setSelectedClient(client);
                            }}
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Edit">
                          <ActionIcon
                            onClick={() => {
                              setSelectedClient(client);
                              setIsCreateModalOpen(true);
                            }}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete">
                          <ActionIcon
                            color="red"
                            onClick={() => setClientToDelete(client)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>

          <Group position="apart" className={styles.pagination}>
            <Select
              value={pageSize.toString()}
              onChange={(value) => setPageSize(Number(value))}
              data={[
                { value: '25', label: '25 per page' },
                { value: '50', label: '50 per page' }
              ]}
              className={styles.select}
            />
            <Pagination
              total={totalPages}
              value={page}
              onChange={setPage}
            />
          </Group>
        </Stack>
      </Box>

      <Modal
        opened={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedClient(null);
        }}
        title={selectedClient ? 'Edit Client' : 'New Client'}
        size="xl"
      >
        <div className={styles.modalContent}>
          <ClientForm
            client={selectedClient}
            onClose={() => {
              setIsCreateModalOpen(false);
              setSelectedClient(null);
            }}
          />
        </div>
      </Modal>

      <Drawer
        opened={!!selectedClient && !isCreateModalOpen}
        onClose={() => setSelectedClient(null)}
        title={<Title order={3}>Client Details</Title>}
        padding="md"
        size="xl"
        position="right"
      >
        <div className={styles.drawerContent}>
          {selectedClient && (
            <ClientDetails
              client={selectedClient}
              onClose={() => setSelectedClient(null)}
            />
          )}
        </div>
      </Drawer>

      <DeleteConfirmationModal
        isOpen={!!clientToDelete}
        onClose={() => setClientToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Client"
        message={`Are you sure you want to delete ${clientToDelete?.first_name} ${clientToDelete?.last_name}? This action cannot be undone.`}
      />
    </div>
  );
}

export default ClientsList; 